import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import _ from 'lodash';

import { AuthService } from '@shared/services/auth.service';
import { HostModeService } from '@shared/services/hostMode.service';
import { ToastrService } from '@shared/services/toastr.service';
import { finalize, forkJoin, Observable } from 'rxjs';
import { Site } from '@shared/models/site';
import { Group, ParamsGroup, Visibility } from '@shared/services/brain-parameters.service';
import { DirtyStateService } from '@shared/services/dirty-state/dirty-state.service';
import { HelpComponent } from '@shared/components/help/help.component';
import { NgIf } from '@angular/common';
import { BrainParametersComponent } from '@shared/components/brain-parameters/brain-parameters.component';
import { SpinnerComponent } from '@shared/components/spinner/spinner.component';
import { SiteBrainParameter, OrganizationSiteService } from '@shared/services/organization-site.service';

@Component({
  selector: 'app-brain-overrides',
  templateUrl: './brain-overrides.component.html',
  styleUrl: './brain-overrides.component.scss',
  imports: [HelpComponent, NgIf, BrainParametersComponent, SpinnerComponent, TranslatePipe],
})
export class BrainOverridesComponent implements OnInit, OnDestroy {
  lang: string;

  @Input({ required: true }) visibility: Visibility;
  @Input({ required: true }) site: Site;
  @Input() showWarning: boolean = true;
  @Input() showVisibility: boolean = false;
  @Input() enableSearch: boolean = true;
  @Input({ required: true }) brainParametersSource: (visibility: Visibility) => Observable<SiteBrainParameter[]>;
  @Input({ required: true }) brainParametersGroupsSource: () => Observable<Group[]>;

  paramsGroups: ParamsGroup[] = [];
  brainParameters: Map<string, SiteBrainParameter>;
  overrides: Record<string, any> = {};
  previousOverrides: Record<string, any> = {};
  hasError = false;
  editMode: boolean = false;
  isLoading = false;

  constructor(
    private readonly siteService: OrganizationSiteService,
    private readonly toastr: ToastrService,
    private readonly auth: AuthService,
    public readonly hostModeService: HostModeService,
    private readonly dirtyState: DirtyStateService,
  ) {
  }

  ngOnInit() {
    this.lang = this.auth.getCurrentLanguage();
    if (this.site.domainName) this.getBrainParams();
  }

  ngOnDestroy(): void {
    this.dirtyState.clear();
  }

  available(visibility: Visibility) {
    return visibility == 'advanced'
      ? this.hostModeService.isAdvanced(this.site.mode)
      : this.hostModeService.isExpert(this.site.mode);
  }

  toggleEditMode() {
    if (!this.available(this.visibility))
      return this.hostModeService.promptSubscribeToMode('SiteModeNotEnabled.' + this.visibility);

    if (this.editMode) {
      this.saveOverrides();
    } else {
      this.editMode = true;
      this.dirtyState.setDirty();
    }
  }

  saveOverrides() {
    this.isLoading = true;
    this.siteService.setBrainOverrides(
      this.site.domainName,
      this.visibility,
      _.mapValues(
        _.pickBy(this.overrides,v => v!==null && v!==undefined && v!==''),
        (v, actor) => this.formatValue(actor, v)))
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(() => {
        this.toastr.success();
        this.endEdit();
        this.getBrainParams();
      });
  }

  endEdit() {
    this.editMode = false;
    this.hasError = false;
    this.dirtyState.clear();
  }

  cancelUpdate() {
    this.endEdit();
    this.overrides = { ...this.previousOverrides };
  }

  getBrainParams() {
    forkJoin({
      groups: this.brainParametersGroupsSource(),
      brainParameters: this.brainParametersSource(this.visibility),
      brainOverrides: this.siteService.getBrainOverrides(this.site.domainName),
    }).subscribe(({ groups, brainParameters, brainOverrides }) => {
      this.paramsGroups = this.buildBrainParametersTree(groups, brainParameters);
      this.brainParameters = new Map<string, SiteBrainParameter>();
      brainParameters.forEach(t => this.brainParameters.set(t.uid, t));

      const availableActors = new Set(brainParameters.map(t => t.uid));

      this.overrides = _.pickBy(brainOverrides, (_, actor) => availableActors.has(actor));
      this.previousOverrides = { ...this.overrides };
    });
  }

  buildBrainParametersTree(groups: Group[], brainParameters: SiteBrainParameter[]): ParamsGroup[] {
    const paramsBySubGroupUid = _.groupBy(brainParameters, p => p.subGroupUid);
    return groups.map((group) => ({
      ...group,
      subGroups: group.subGroups
        .map((subGroup) => ({
          ...subGroup,
          params: paramsBySubGroupUid[subGroup.uid],
        })),
    }));
  }

  formatValue(parameterUid: string, value: any): number {
    if (value === false) {
      return 0;
    } else if (value === true) {
      const brainTunable = this.brainParameters.get(parameterUid);
      return brainTunable.defaultValue || 0.1;
    } else {
      return value;
    }
  }
}
